import { getHelper } from '@/devtools/utils';
import { SIMPLE_CHOICE } from '@/features/ChoicesLists/Simple/types';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import { resContractDehydrationDefinitions } from '@/features/Sparten/res/contractDehydration';
import { resInputDehydrationDefinitions } from '@/features/Sparten/res/inputDehydration';
import type { InputStore } from '@/features/Sparten/res/useInputStore';
import { useResInputStore } from '@/features/Sparten/res/useInputStore';
import { featureFilterFunction } from '@/features/Sparten/res/utils/featureFilterFunction';
import type { SpartenConfig } from '@/features/Sparten/types';
import { SORTABLE_ATTRIBUTES } from '@/features/Tariffs/sorting';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import boolean from '@/utils/formatters/boolean';
import { formatCurrency } from '@/utils/formatters/euro';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import { formatYears } from '@/utils/formatters/years';
import { useStorableState } from '@/utils/useStorableState/useStorableState';

import {
  KEEP_DATA_FROM_QUOTE_MAPPING,
  keepDataFromQuoteMappings,
} from '@/features/Sparten/shared/keepDataFromQuoteMappings';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { hasPartner } from '@/features/Sparten/res/utils/hasPartner';
import {
  previousDamageSchema,
  ResPreviousDamage,
  useResContractStore,
} from '@/features/Sparten/res/useContractStore';
import image from './assets/bg.jpg';
import { resVersicherungsangabenConfig } from '@/features/Sparten/res/versicherungsangabenConfig';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/res',

  props: {
    name: 'Rechtsschutzversicherung',
    sparte: 'res',
    image,
  },

  general: {
    distribution: {
      channel: true,
      pooling: true,
    },

    maxSimultaneousCount: 3,

    hasRisikoDEMVerCertificate: true,

    versicherungsangabenConfig: resVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
      versichertePerson: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: ['geburtsdatum', 'adresse.plz'],
        versichertePerson: ['geburtsdatum', 'adresse.plz'],
      },
    },

    profileFields: [
      'selbstbeteiligung',
      'tarifgruppe',
      'berufsstatus',
      'leistungsumfang.privat',
      'leistungsumfang.beruf',
      'leistungsumfang.verkehr',
      'leistungsumfang.hasMehrereFahrzeuge',
      'leistungsumfang.anzahlFahrzeuge',
      'leistungsumfang.fahrzeugtyp',
      'leistungsumfang.mieten',
      'leistungsumfang.vermietung',
      'leistungsumfang.bruttojahresmieteBetrag1',
      'leistungsumfang.bruttojahresmieteBetrag2',
      'leistungsumfang.bruttojahresmieteBetrag3',
      'leistungsumfang.vermietungObjekteAnzahl',
    ],

    supportMitversichertenPartner: true,
    hasPartner,

    keepDataFromQuote: {
      clients: {
        versicherungsnehmer: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE],
        ],
        mitversicherterPartner: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE],
        ],
      },
    },

    featureFilterFunction,

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = useResInputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          resInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state: Record<string, unknown>) =>
      dehydrateRequestData(state, resContractDehydrationDefinitions),

    getSortableAttributes: () => [
      SORTABLE_ATTRIBUTES.DECKUNGSSUMME,
      SORTABLE_ATTRIBUTES.SELBSTBETEILIGUNG,
      SORTABLE_ATTRIBUTES.LAUFZEIT,
    ],

    verifyZipcodeCityCombination: true,

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Privat',
          fieldPath: 'leistungsumfang.privat',
          formatter: boolean,
          inputFieldAsFallback: true,
        },
        {
          label: 'Beruf',
          fieldPath: 'leistungsumfang.beruf',
          formatter: boolean,
          inputFieldAsFallback: true,
          requirement: () => useResInputStore().leistungsumfang.privat,
        },
        {
          label: 'Verkehr',
          fieldPath: 'leistungsumfang.verkehr',
          formatter: boolean,
          inputFieldAsFallback: true,
        },
        {
          label: 'Wohnen',
          fieldPath: 'leistungsumfang.mieten',
          formatter: boolean,
          inputFieldAsFallback: true,
        },
        {
          label: 'Vermietung',
          fieldPath: 'leistungsumfang.vermietung',
          formatter: boolean,
          inputFieldAsFallback: true,
        },
        {
          label: 'Laufzeit',
          fieldPath: 'laufzeit',
          formatter: formatYears,
        },
        {
          label: 'Deckungssumme',
          fieldPath: 'deckungssumme',
          inputFormatter: formatChoice(SIMPLE_CHOICE.DECKUNGSSUMME),
          formatter: (amount: number | string) => formatCurrency(amount, true),
        },
        {
          label: 'Selbstbeteiligung',
          fieldPath: 'selbstbeteiligung',
          formatter: formatCurrency,
          inputFormatter: formatChoice(SIMPLE_CHOICE.SELBSTBETEILIGUNG),
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
      ],
    },

    baseTariffComponent: () =>
      import('@/features/Tariffs/BaseTariff/BaseTariff.vue'),

    tariffComponent: () =>
      import(
        '@/features/Sparten/res/components/TariffDetails/TariffDetails.vue'
      ),

    previousDamageComponent: () =>
      import(
        '@/features/Sparten/res/components/PreviousDamage/PreviousDamage.vue'
      ),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('res').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('res').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('res').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('res').showContract(),
        },
      },
    },

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
    blockCompanyClient: true,

    quotePostLoadCallback: (data) => {
      if (
        data.contractData !== undefined &&
        typeof data.contractData.vorschaden === 'object' &&
        data.contractData.vorschaden !== null &&
        'list' in data.contractData.vorschaden
      ) {
        const store = useResContractStore();
        const vorschaedenList = data.contractData.vorschaden
          .list as ResPreviousDamage[];
        const defaultPreviousDamageSchema = previousDamageSchema.getDefault();

        vorschaedenList.forEach((_, index) => {
          if (!('entschaedigungszahlung' in store.vorschaden.list[index])) {
            store.vorschaden.list[index].entschaedigungszahlung =
              defaultPreviousDamageSchema.entschaedigungszahlung;
          }

          if (!('schadensmeldung' in store.vorschaden.list[index])) {
            store.vorschaden.list[index].schadensmeldung =
              defaultPreviousDamageSchema.schadensmeldung;
          }

          if (!('bereich' in store.vorschaden.list[index])) {
            store.vorschaden.list[index].bereich =
              defaultPreviousDamageSchema.bereich;
          }
        });
      }
    },
  },

  // Wizard steps
  steps: {
    input: {
      component: () => import('@/features/Sparten/res/views/InputView.vue'),
    },

    offers: {
      component: () => import('@/features/Sparten/res/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/res/views/Comparison.vue'),
    },

    contract: {
      component: () => import('@/features/Sparten/res/views/ContractView.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};

export default config;
